import {
  Component,
  Input,
  OnInit,
  ElementRef,
  ViewChild,
  EventEmitter,
  Output,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { TwilioModalComponent } from '../twilio-modal/twilio-modal.component';
import { environment } from '../../../environments/environment';
import { ActivatedRoute, Router } from '@angular/router';
import { QrCodeService } from '../services/qr-code.service';
import { Console } from 'console';
import { ApartmentService } from '../services/apartment.service';
import { BlockUIService } from '../components/block-ui/block-ui.service';
import { SignalrService } from '../services/signalr.service';
import { Subscription } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { MeasurementFormUploadMessage, Status } from '../types/measurement-form-upload-message';

@Component({
  standalone: false,
  selector: 'app-twilio-button',
  templateUrl: './twilio-button.component.html',
  styleUrls: ['./twilio-button.component.scss'],
})
export class TwilioButtonComponent implements OnInit {
  @Input() text = 'Text Me a Link';
  @Input() apartmentComplexId!: number;
  @Input() apartmentId!: number;
  shortUrl: string | null = null;
  qrCodeImage: string | null = null;
  logoPath = 'assets/images/qwikkit-logo.png';
  @Output() measurementFormDataLoaded = new EventEmitter<any>();
  enableMeasurementFormUpload = false;
  @Output() isTokenInvalid = new EventEmitter<boolean>();
  @ViewChild('qrCanvas', { static: false }) qrCanvas!: ElementRef<HTMLCanvasElement>;
  private signalrError = false;
  private errorSubscription: Subscription;
  private startingSubscription: Subscription;

  constructor(
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private qrCodeService: QrCodeService,
    private apartmentService: ApartmentService,
    private signalrService: SignalrService,
    private toastService: ToastrService,
    private blockUI: BlockUIService
  ) {}

  ngOnInit(): void {
    this.generateQrCode();

    this.errorSubscription = this.signalrService.error$.subscribe(
      (error: any) => {
        this.signalrError = true;
        console.warn(error);
      },
      (error: any) => {
        this.signalrError = true;
        console.error('errors$ error', error);
      }
    );

    this.startingSubscription = this.signalrService.starting$.subscribe(
      () => {
        console.log('Signalr service has been started');
      },
      () => {
        console.warn('Signalr service failed to start!');
      }
    );

    try {
      if (!this.signalrService.isConnected()) {
        console.log('Action: signalrService.start');
        this.signalrService.connect();
      }
    } catch (err) {
      this.signalrError = true;
      console.error('Error starting the signalr connection to a hub.', err);
    }
  }

  generateQrCode(): void {
    this.blockUI.start('Generating QR Code...');
    this.qrCodeService
      .generateQrCode(this.apartmentComplexId, this.apartmentId)
      .subscribe(
        (response) => {
          this.shortUrl = `${response.shortUrl}`;
          this.qrCodeImage = response.qrCodeBase64;
          setTimeout(() => {
            this.overlayLogo();
          }, 100);
          this.blockUI.stop();
        },
        (error) => {
          console.error('Error generating QR code:', error);
          this.blockUI.stop();
        }
      );
  }

  overlayLogo(): void {
    if (!this.qrCodeImage || !this.qrCanvas) {
return;
}

    const canvas = this.qrCanvas.nativeElement;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
return;
}

    const qrImg = new Image();
    qrImg.src = this.qrCodeImage;
    qrImg.crossOrigin = 'Anonymous';
    qrImg.onload = () => {
      canvas.width = qrImg.width;
      canvas.height = qrImg.height;
      canvas.style.width = '70%';
      canvas.style.height = '70%';

      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.drawImage(qrImg, 0, 0, canvas.width, canvas.height);

      const logoImg = new Image();
      logoImg.src = this.logoPath;
      logoImg.crossOrigin = 'Anonymous';
      logoImg.onload = () => {
        const logoSize = canvas.width / 5;
        const x = (canvas.width - logoSize) / 2;
        const y = (canvas.height - logoSize) / 2;

        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');

        if (!tempCtx) {
return;
}

        tempCanvas.width = logoSize;
        tempCanvas.height = logoSize;

        tempCtx.fillStyle = 'white';
        tempCtx.fillRect(0, 0, logoSize, logoSize);

        tempCtx.drawImage(logoImg, 0, 0, logoSize, logoSize);

        ctx.drawImage(tempCanvas, x, y, logoSize, logoSize);
      };
    };
  }
  detectScanEvent(): void {
    this.route.queryParams.subscribe((params) => {
      if (params['scanned'] === 'true') {
        this.router.navigate([], { queryParams: {} });
      }
    });
  }

  handleShortUrlClick(event: Event): void {
    event.preventDefault();

    if (!this.shortUrl) {
      console.error('Short URL is missing.');
      return;
    }
    this.qrCodeService.resolveShortUrl(this.shortUrl).subscribe(
      (response) => {
        if (response.isValidToken) {
          window.open(response.resolvedUrl, '_blank');
          this.isTokenInvalid.emit(false);
        }
      },
      (error) => {
        console.error('Error resolving short URL:', error);
        if (error.status === 401) {
          this.isTokenInvalid.emit(true);
        }
      }
    );
  }

  handleFreshClick(): void {
    this.generateQrCode();
    this.waitForImage();
    this.isTokenInvalid.emit(false);
  }

  handleMeasurementFormUpload(): void {
    this.blockUI.start('Getting Measurement Form Data...');
    this.apartmentService
      .getMeasurementFormData(this.apartmentComplexId, this.apartmentId)
      .subscribe(
        (response) => {
          this.measurementFormDataLoaded.emit(response);
          this.enableMeasurementFormUpload = true;
          this.blockUI.stop();
        },
        (error) => {
          console.error('Error getting measurement form data:', error);
          this.blockUI.stop();
          this.enableMeasurementFormUpload = false;
        }
      );
  }

  openTwilioModal(): void {
    this.dialog.open(TwilioModalComponent, {
      width: '400px',
      data: {},
    });
  }

  waitForImage(): void {
    // Timeout ID for cleanup
    let receivedMsgTimeoutId: ReturnType<typeof setTimeout> | undefined;

    console.log('SignalR waiting for apartment ID:', this.apartmentId);

    // Subscribe to SignalR observable
    const signalrServiceSubscription = this.signalrService.measurementFormUploadMessage
        .pipe(
            filter((msg: MeasurementFormUploadMessage) => {
                const matches = String(msg.apartmentId) === String(this.apartmentId);
                return matches;
            })
        )
        .subscribe({
            next: (msg: MeasurementFormUploadMessage) => {
                // Clear timeout if a message is received
                if (receivedMsgTimeoutId) {
                  clearTimeout(receivedMsgTimeoutId);
                  receivedMsgTimeoutId = undefined;
                }

                if (msg.currentStatus === Status.Completed || msg.currentStatus === Status.Error) {

                  signalrServiceSubscription.unsubscribe();
                  this.blockUI.stop();

                  if (msg.currentStatus === Status.Completed) {
                    this.handleMeasurementFormUpload();
                  }

                  console.log('Measurement form upload finished. Status:', msg.currentStatus);

                  const modalMsg = msg.currentStatus === Status.Completed
                    ? `Measurement form successfully uploaded for apartment id ${msg.apartmentId}.`
                    : `Measurement form upload failed for apartment id ${msg.apartmentId}.`;

                  const toastOptions = {
                    positionClass: 'toast-bottom-right' as any,
                    timeOut: 0,
                    tapToDismiss: true
                  };

                  if (msg.currentStatus === Status.Completed) {
                    //this.toastService.success(modalMsg, 'Please click to close', toastOptions);
                  } else {
                    this.toastService.error(modalMsg, 'Please click to close', toastOptions);
                  }
                }
            },
            error: (err) => {
                console.error('SignalR error:', err);
                cleanup();
            }
        });

    // Cleanup function to avoid duplication
    const cleanup = () => {
        if (receivedMsgTimeoutId) {
            clearTimeout(receivedMsgTimeoutId);
            receivedMsgTimeoutId = undefined;
        }
        signalrServiceSubscription.unsubscribe();
        this.blockUI.stop();
    };

    receivedMsgTimeoutId = setTimeout(() => {
        console.warn('Timed out waiting for SignalR message.');
        cleanup();
    }, 900000);
  }

}
